import { useTranslator } from '@Localization';
import { observer } from 'mobx-react-lite';
import { ReactElement } from 'react';
import { GTable, IGColumnProps } from '../../../../../../__legacy__/components/table/GTable';
import { enumDirectionToStringOrder, stringOrderToEnumDirection } from '../../../../../../__legacy__/components/table/TableHelpers';
import { SortingDirection } from '../../../../../../__legacy__/models/sortingDirection.model';
import { IWorkPerformanceStats } from '../models/WorkPerformance';

interface IProps {
  items: IWorkPerformanceStats[];
  orderBy: string;
  orderDirection: SortingDirection | undefined;
  onSortChange: (orderBy: string, orderDirection: SortingDirection | undefined) => void;
}

export const WorkPerformanceStatsTable = observer((props: IProps): ReactElement => {
  const { t } = useTranslator();
  const { orderBy, orderDirection, onSortChange, items } = props;

  const renderAccuracy = (item: IWorkPerformanceStats): ReactElement => (
    <span className='accuracy-cell'>{item.totalAdditionsAndModifications !== 0 ? <>{getAccuracyPercentage(item.accuracy)}</> : <>&mdash;</>}</span>
  );

  const getAccuracyPercentage = (accuracy: number): string => `${(Math.min(Math.max(accuracy, 0), 1) * 100).toFixed(2)} %`;

  const columns: IGColumnProps<IWorkPerformanceStats>[] = [
    {
      sortBy: 'email',
      cellClassName: 'stats-cell-bold',
      headerName: t('projects.details.stats.work_performance.headers.email'),
      renderer: (x) => x.email,
    },
    {
      sortBy: 'totalAdditionsAndModifications',
      headerName: t('projects.details.stats.work_performance.headers.totalAdditionsAndModifications'),
      headerTooltip: t('projects.details.stats.work_performance.tooltips.totalAdditionsAndModifications'),
      renderer: (x) => x.totalAdditionsAndModifications,
    },
    {
      sortBy: 'awaitingReview',
      headerName: t('projects.details.stats.work_performance.headers.awaitingReview'),
      headerTooltip: t('projects.details.stats.work_performance.tooltips.awaitingReview'),
      renderer: (x) => x.awaitingReview,
    },
    {
      sortBy: 'accepted',
      headerName: t('projects.details.stats.work_performance.headers.accepted'),
      headerTooltip: t('projects.details.stats.work_performance.tooltips.accepted'),
      renderer: (x) => x.accepted,
    },
    {
      sortBy: 'accuracy',
      headerName: t('projects.details.stats.work_performance.headers.accuracy'),
      headerTooltip: t('projects.details.stats.work_performance.tooltips.accuracy'),
      renderer: renderAccuracy,
    },
  ];

  const handleSortChange = (orderBy: string, orderDirection: string): void => {
    onSortChange(orderBy, stringOrderToEnumDirection(orderDirection));
  };

  return (
    <GTable
      columns={columns}
      items={items}
      isLoading={false}
      noItemsPlaceholder={t('projects.details.stats.no_statistics_have_been_generated_yet')}
      onSortChanged={handleSortChange}
      sortingModel={[{ orderBy, orderType: enumDirectionToStringOrder(orderDirection) }]}
    />
  );
});
